import { deepCopy, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { Table } from 'primeng/table';
import { Component, OnInit, ViewChild, Injector, Input } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { HubLabelSetupService } from '../../service/hub-label-setup.service';
import { PageInfo } from 'src/app/core/models/page-info';
import { throwError } from 'rxjs';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';

@Component({
  selector: 'laas-hub-label-setup',
  templateUrl: './hub-label-setup.component.html',
  styleUrls: ['./hub-label-setup.component.scss']
})
export class HubLabelSetupComponent
  extends SessionBaseComponent
  implements OnInit {
  readonly viewPermissionCode = PermissionConstants.HubLabelSetup;
  currentLang: string = this.laasSetting.language;
  // Status for the Query Form is open or not
  isSearchFormOpen: boolean = true;
  // Status for the loading circle on the table
  isLoading: boolean = false;
  // Status for enabling a user to edit the data on the table
  isInEditMode: boolean = false;

  // The FormGroup class for the Query Form part
  searchForm: FormGroup;

  // Get the table class underlying the primeNG table
  @ViewChild('dataTable') dataTable!: Table;
  // All the columns of the table have
  tableColumns: any = [
    { field: 'orgId', header: 'HubLabelSetup.ColumnHeaders.ORGId', sort: true },
    { field: 'hubVendorCode', header: 'HubLabelSetup.ColumnHeaders.HubVendorCode', sort: true },
    { field: 'custSku', header: 'HubLabelSetup.ColumnHeaders.CustSku', sort: true },
    { field: 'labelKey', header: 'HubLabelSetup.ColumnHeaders.LabelKey', sort: true },
    { field: 'hubLabelSetupId', header: 'HubLabelSetup.ColumnHeaders.HubLabelSetupId', sort: true },
    { field: 'createdBy', header: 'HubLabelSetup.ColumnHeaders.CreatedBy', sort: true },
    { field: 'creationDate', header: 'HubLabelSetup.ColumnHeaders.CreationDate', sort: true },
    { field: 'lastUpdatedBy', header: 'HubLabelSetup.ColumnHeaders.LastUpdatedBy', sort: true },
    { field: 'lastUpdateDate', header: 'HubLabelSetup.ColumnHeaders.LastUpdateDate', sort: true },
  ];

  // The columns user choose to display
  selectedTableColumns: any = [];
  // Current data on the table
  tableData: any = [];
  // The data retrieved from the back-end
  backupTableData: any = [];
  // User current selected row on the table
  selectedRow: any;
  // User last selected row on the table
  previousSelectedRow: any;
  // For data key to distinguish the added rows on the table
  newHubLabelSetupId: number = 0;

  // Table paging info
  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 1,
    totalRecords: 0,
    totalPages: 0,
  };

  // Table sorting info
  sortInfo: { property: string; direction: number; } = {
    property: "hubLabelSetupId",
    direction: -1
  };

  // Records for user's operation on the table
  createHubLabelSetupList: any = [];
  updatedHubLabelSetupList: any = [];
  deletedHubLabelSetupIdList: any = [];

  constructor(
    private formBuilder: FormBuilder,
    private hubLabelSetupService: HubLabelSetupService,
    private injector: Injector
  ) {
    super(injector);
    this.searchForm = this.formBuilder.group({
      orgId: null,
      hubVendorCode: null,
      custSku: null,
      labelKey: null,
    });
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.selectedTableColumns = this.tableColumns;
  }

  @Input() get selectedColumns(): any[] {
    return this.selectedTableColumns;
  }

  // Set the columns user want to show
  set selectedColumns(cols: any[]) {
    if (!isNullOrEmpty(this.tableColumns)) {
      this.selectedTableColumns = this.tableColumns.filter((col: any) => cols.includes(col));
    }
  }

  searchBtnClick() {
    //  Check if the label key in search form is positive integer
    if (!isNullOrEmpty(this.searchForm.value.labelKey) && !this.checkLabelKeyIsInRightFormat(this.searchForm.value.labelKey)) {
      this.toastService.warn(this.translateService.instant('HubLabelSetup.Toast.WrongLabelKeyFormat'));
      return;
    }

    this.isInEditMode = false;

    this.isSearchFormOpen = false;
    this.pageInfo.pageIndex = 1;
    this.isLoading = true;
    this.sendSearchRequest();
  }

  sendSearchRequest() {
    this.resetData();
    this.hubLabelSetupService.getHubLabelSetups(this.searchForm.value, this.pageInfo, this.sortInfo).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.dataTable.cancelRowEdit(this.selectedRow)
        if (!isNullOrEmpty(response)) {
          // Update table
          this.tableData = deepCopy(response.rows)
          this.backupTableData = deepCopy(this.tableData);
          this.selectedRow = this.tableData[0];
          if (this.isInEditMode) {
            this.dataTable.initRowEdit(this.selectedRow);
          }
          this.previousSelectedRow = this.selectedRow;
          this.updateTableHighlight(this.selectedRow);

          // Change page Info
          this.pageInfo.totalRecords = response.total;
          this.pageInfo.totalPages = this.pageInfo.totalRecords / this.pageInfo.pageSize;
        }
        else {
          throw throwError(response);
        }
      },
      error: response => {
        // Clear table
        this.tableData = [];
        this.backupTableData = [];
        this.selectedRow = null;
        this.previousSelectedRow = this.selectedRow;
        this.pageInfo.totalRecords = 0;
        this.pageInfo.totalPages = 0;
        this.toastService.error(this.translateService.instant('HubLabelSetup.Toast.SaveFail') + response.error.errorCode)
      }
    });
  }

  addNewRow(): void {
    // Prohibit user to add a new row before user fill the required data well
    if (!this.isUserInputValid(this.previousSelectedRow))
      return;

    // Add a new row to table's first row
    this.tableData.splice(0, 0, {
      'orgId': null, 'hubVendorCode': null, 'custSku': null, 'labelKey': null, 'dataKey': `--${this.newHubLabelSetupId++}`,
      'hubLabelSetupId': null, 'createdBy': this.userService.userInfo.userName, 'creationDate': null, 'lastUpdatedBy': this.userService.userInfo.userName, 'lastUpdateDate': null
    });

    // Change the current editing row to the added row
    this.dataTable.cancelRowEdit(this.selectedRow);
    this.selectedRow = this.tableData[0];
    this.previousSelectedRow = this.selectedRow;
    this.dataTable.initRowEdit(this.selectedRow);
    this.updateTableHighlight(this.selectedRow);
  }

  //  Check if the label key is positive integer
  checkLabelKeyIsInRightFormat(labelKey: any): boolean {
    if (isNaN(labelKey) || labelKey.includes('.') || labelKey.includes('e') || parseInt(labelKey) < 0) {
      return false;
    }
    return true;
  }

  deleteSelectedRow(): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('HubLabelSetup.Confirm.Delete'),
      header: this.translateService.instant('HubLabelSetup.Confirm.Header'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.dataTable.cancelRowEdit(this.selectedRow);

        const selectedRowIndex = this.tableData.indexOf(this.selectedRow);
        if (selectedRowIndex > -1) {
          this.addToDeletedList(this.tableData[selectedRowIndex].hubLabelSetupId);

          // Remove deleted row from table
          this.tableData.splice(selectedRowIndex, 1);

          // Change selected row
          if (selectedRowIndex != this.tableData.length) {
            // Change user current selected row to the next row of the deleted row
            this.selectedRow = this.tableData[selectedRowIndex];
          }
          else { // Deleted row is the last row of the current page
            // Change user current selected row to the previous row of the deleted row
            this.selectedRow = this.tableData[selectedRowIndex - 1];
          }
          this.previousSelectedRow = this.selectedRow;
          this.updateTableHighlight(this.selectedRow);

          this.dataTable.initRowEdit(this.selectedRow);
          this.toastService.success(this.translateService.instant("HubLabelSetup.Toast.DeleteSuccess"));
        }
      }
    });
  }

  saveChanges(): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('HubLabelSetup.Confirm.Save'),
      header: this.translateService.instant('HubLabelSetup.Confirm.Header'),
      icon: 'pi pi-info-circle',
      accept: () => {
        // Prohibit user to save row if user doesn't fill the required data on the newly added row
        if (!this.isUserInputValid(this.selectedRow))
          return;

        this.isLoading = true;

        // Check user's operations on the table
        this.checkDataChanges();
        const savedData =
        {
          createHubLabelSetupList: this.createHubLabelSetupList.reverse(),
          deletedHubLabelSetupIdList: this.deletedHubLabelSetupIdList,
          updatedHubLabelSetupList: this.updatedHubLabelSetupList
        };
        this.hubLabelSetupService.saveChanges(savedData).subscribe({
          next: () => {
            this.dataTable.cancelRowEdit(this.selectedRow);
            this.isInEditMode = false;

            this.sendSearchRequest();
            this.toastService.success(this.translateService.instant("HubLabelSetup.Toast.SaveSuccess"));
          },
          error: response => {
            this.toastService.error(this.translateService.instant('HubLabelSetup.Toast.SaveFail') + response.error.errorCode)
          }
        });
      }
    });
  }

  restoreTableData(): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('HubLabelSetup.Confirm.Restore'),
      header: this.translateService.instant('HubLabelSetup.Confirm.Header'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.resetData();

        // Update table
        this.dataTable.cancelRowEdit(this.selectedRow);
        this.tableData = deepCopy(this.backupTableData);
        this.selectedRow = this.tableData[0];
        this.previousSelectedRow = this.selectedRow;
        this.dataTable.initRowEdit(this.selectedRow);
        this.updateTableHighlight(this.selectedRow);

        this.toastService.success(this.translateService.instant("HubLabelSetup.Toast.RestoreSuccess"));
      }
    });
  }

  switchEditMode() {
    if (this.isInEditMode) {
      this.confirmationService.confirm({
        message: this.translateService.instant('HubLabelSetup.Confirm.Exit'),
        header: this.translateService.instant('HubLabelSetup.Confirm.Header'),
        icon: 'pi pi-info-circle',
        accept: () => {
          this.isInEditMode = false;
          this.resetData();

          // Update table to last saved data
          this.tableData = deepCopy(this.backupTableData);
          this.dataTable.cancelRowEdit(this.selectedRow);
          this.selectedRow = this.tableData[0];
          this.previousSelectedRow = this.selectedRow;
          this.updateTableHighlight(this.selectedRow);

          this.toastService.success(this.translateService.instant("HubLabelSetup.Toast.ExitSuccess"));
        }
      });
    }
    else {
      //Check if a row is selected
      if (this.selectedRow) {
        this.dataTable.initRowEdit(this.selectedRow);
      }
      this.isInEditMode = true;
    }
  }

  onRowSelect(rowData: any) {
    if (this.isInEditMode) {
      // Keep the selectedRow variable is pointing at the user's selected row
      // Whether is select or deselect
      this.selectedRow = rowData;

      //Reselect after user click on the same row
      if (this.previousSelectedRow != null && this.selectedRow.dataKey == this.previousSelectedRow.dataKey) {
        this.updateTableHighlight(this.previousSelectedRow);
        return;
      }

      if (this.isUserInputValid(this.previousSelectedRow)) {
        // Change the current selected row to the user's selected row
        this.dataTable.cancelRowEdit(this.previousSelectedRow);
        this.dataTable.initRowEdit(this.selectedRow);
        this.previousSelectedRow = this.selectedRow;
      }
      else {
        // Rollback the selected row to the row which doesn't fill the required data well
        this.selectedRow = this.previousSelectedRow;
        this.updateTableHighlight(this.previousSelectedRow);
      }
    }
    else {
      this.previousSelectedRow = this.selectedRow;
    }
  }

  pageChange(event: any) {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / event.rows + 1;
    this.isLoading = true;
    this.sendSearchRequest();
  }

  tableSorting(event: any) {
    if (this.tableData.length != 0) {
      this.sortInfo.property = event.field;
      this.sortInfo.direction = event.order;
      this.isLoading = true;
      this.sendSearchRequest();
    }
  }

  // Check if all the required data does the user filled
  // And ensure that's in correct format
  private isUserInputValid(rowData: any): boolean {
    if (rowData) {
      if (((isNullOrEmpty(rowData.orgId)) ||
        (isNullOrEmpty(rowData.hubVendorCode)) ||
        (isNullOrEmpty(rowData.custSku)) ||
        (isNullOrEmpty(rowData.labelKey) && !isNaN(rowData.labelKey)))) {
        this.toastService.warn(this.translateService.instant('HubLabelSetup.Toast.EmptyDataError'));
        return false;
      }
      else if (!this.checkLabelKeyIsInRightFormat(rowData.labelKey)) {
        this.toastService.warn(this.translateService.instant('HubLabelSetup.Toast.WrongLabelKeyFormat'));
        return false;
      }
    }

    return true;
  }

  private resetData(): void {
    // Clear user changed action
    this.newHubLabelSetupId = 0;
    this.createHubLabelSetupList = [];
    this.updatedHubLabelSetupList = [];
    this.deletedHubLabelSetupIdList = [];
    this.dataTable._first = 0;
    this.dataTable.resetScrollTop();
  }

  private checkDataChanges(): void {
    let saveDataIndex = 0;

    if (saveDataIndex < this.tableData.length) {
      // Check if any added row by dataKey property
      // Added row's dataKey format : --id
      for (const tableData of this.tableData) {
        if (tableData.dataKey[0] != '-') {
          break;
        }
        this.addToCreateList(tableData);
        saveDataIndex++;
      }

      // Compare backup table data to current table data
      for (const backupData of this.backupTableData) {
        if (this.deletedHubLabelSetupIdList.indexOf(backupData.hubLabelSetupId) == -1) {
          if (JSON.stringify(this.tableData[saveDataIndex]) != JSON.stringify(backupData)) {
            this.addToUpdateList(this.tableData[saveDataIndex]);
          }
          saveDataIndex++;
        }
      }

    }
  }

  private addToCreateList(rowData: any): void {
    var newHubLabelSetup = {
      'createdBy': rowData.createdBy,
      'orgId': rowData.orgId,
      'hubVendorCode': rowData.hubVendorCode,
      'custSku': rowData.custSku,
      'labelKey': rowData.labelKey
    };
    this.createHubLabelSetupList.push(newHubLabelSetup);
  }

  private addToUpdateList(rowData: any): void {
    var updatedHubLabelSetup = {
      'lastUpdatedBy': this.userService.userInfo.userName,
      'hubLabelSetupId': rowData.hubLabelSetupId,
      'orgId': rowData.orgId,
      'hubVendorCode': rowData.hubVendorCode,
      'custSku': rowData.custSku,
      'labelKey': rowData.labelKey
    };
    this.updatedHubLabelSetupList.push(updatedHubLabelSetup);
  }

  private addToDeletedList(hubLabelSetupId: number): void {
    if (!isNullOrEmpty(hubLabelSetupId)) {
      this.deletedHubLabelSetupIdList.push(hubLabelSetupId);
    }
  }

  //Control the selection highlighting back to the row
  private updateTableHighlight(rowData: any) {
    if (isNullOrEmpty(rowData))
      return;

    this.dataTable._selection = rowData;
    this.dataTable.selectionKeys = {};
    this.dataTable.selectionKeys[rowData.dataKey] = 1;
    this.dataTable.tableService.onSelectionChange();
  }
}
